import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useLocation, useSearch } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  RadarChart, PolarGrid, PolarAngleAxis, Radar as RechartsRadar,
  ResponsiveContainer, PolarRadiusAxis,
} from "recharts";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import {
  Target, ArrowLeft, ArrowRight, AlertCircle, ChevronDown, ChevronUp, Loader2,
  BookOpen, ExternalLink, X as XIcon,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { buildChartData, normaliseCriterion } from "@/lib/radar-scoring";
import type { RadarConfig, RadarCriterion, RadarEvaluation, RadarCriteriaSet, RadarPrecursorDefinition } from "@shared/schema";

interface CriterionImage {
  id: string;
  storageUrl: string;
  caption?: string | null;
  altText?: string | null;
}
interface CriterionResource {
  id: string;
  url: string;
  label: string;
  description?: string | null;
}
type EnrichedCriterion = RadarCriterion & {
  images?: CriterionImage[];
  resources?: CriterionResource[];
};

interface RadarDetail {
  config: RadarConfig & {
    company?: { id: string; name: string; slug?: string | null } | null;
  };
  criteria: EnrichedCriterion[];
  criteriaSets: RadarCriteriaSet[];
  precursorDefinitions: RadarPrecursorDefinition[];
}


function CriterionInput({
  criterion,
  value,
  onChange,
  multiSelections,
  onMultiSelectChange,
  rangeToValue,
  onRangeToChange,
}: {
  criterion: RadarCriterion;
  value: number;
  onChange: (v: number) => void;
  multiSelections?: boolean[];
  onMultiSelectChange?: (selections: boolean[]) => void;
  rangeToValue?: number;
  onRangeToChange?: (v: number) => void;
}) {
  const min = criterion.minValue ?? 0;
  const max = criterion.maxValue ?? 10;
  const options = criterion.criterionOptions ?? [];
  const unit = criterion.unitLabel ?? "";

  if (criterion.inputType === "toggle") {
    return (
      <div className="flex items-center gap-3 py-3">
        <Switch
          checked={value >= (min + max) / 2}
          onCheckedChange={checked => onChange(checked ? max : min)}
          data-testid={`toggle-criterion-${criterion.id}`}
        />
        <Label
          className="text-sm cursor-pointer"
          onClick={() => onChange(value >= (min + max) / 2 ? min : max)}
        >
          {value >= (min + max) / 2 ? "Meets requirement" : "Does not meet requirement"}
        </Label>
      </div>
    );
  }

  if (criterion.inputType === "radio") {
    const hasOptions = options.length > 0;
    const radioOptions = hasOptions
      ? options.map((label, i) => ({
          value: options.length === 1 ? max : min + Math.round((i / (options.length - 1)) * (max - min)),
          label,
        }))
      : (() => {
          const steps = Math.min(max - min + 1, 5);
          const fallbackLabels = ["Poor", "Below Average", "Average", "Good", "Excellent"];
          return Array.from({ length: steps }, (_, i) => ({
            value: min + Math.round((i / (steps - 1)) * (max - min)),
            label: fallbackLabels[i] ?? `${min + Math.round((i / (steps - 1)) * (max - min))}`,
          }));
        })();
    return (
      <div className="flex flex-wrap gap-2 py-3">
        {radioOptions.map(opt => (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              value === opt.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:border-primary/50 hover:bg-muted"
            }`}
            data-testid={`radio-${criterion.id}-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    );
  }

  if (criterion.inputType === "multi_select") {
    const selections = multiSelections ?? options.map(() => false);
    return (
      <div className="py-3 space-y-2">
        {options.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No options configured for this criterion.</p>
        ) : (
          options.map((opt, i) => (
            <label
              key={i}
              className="flex items-center gap-3 cursor-pointer group"
              data-testid={`multi-select-${criterion.id}-${i}`}
            >
              <div
                className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-all shrink-0 ${
                  selections[i]
                    ? "bg-primary border-primary"
                    : "border-border group-hover:border-primary/50"
                }`}
                onClick={() => {
                  const next = [...selections];
                  next[i] = !next[i];
                  onMultiSelectChange?.(next);
                  onChange(next.filter(Boolean).length);
                }}
              >
                {selections[i] && (
                  <svg viewBox="0 0 12 12" className="h-3 w-3 text-primary-foreground fill-current">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <span
                className="text-sm"
                onClick={() => {
                  const next = [...selections];
                  next[i] = !next[i];
                  onMultiSelectChange?.(next);
                  onChange(next.filter(Boolean).length);
                }}
              >
                {opt}
              </span>
            </label>
          ))
        )}
        {options.length > 0 && (
          <p className="text-xs text-muted-foreground pt-1">
            {selections.filter(Boolean).length} of {options.length} selected
          </p>
        )}
      </div>
    );
  }

  if (criterion.inputType === "number_input") {
    return (
      <div className="py-3">
        <div className="flex items-center gap-2">
          <input
            type="number"
            min={min}
            max={max}
            value={value}
            onChange={e => {
              const v = parseFloat(e.target.value);
              if (!isNaN(v)) onChange(Math.min(max, Math.max(min, v)));
            }}
            className="w-36 h-10 rounded-md border border-input bg-background px-3 text-lg font-semibold text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
            data-testid={`number-input-criterion-${criterion.id}`}
          />
          {unit && <span className="text-sm text-muted-foreground font-medium">{unit}</span>}
        </div>
        {(min !== 0 || max !== 10) && (
          <p className="text-xs text-muted-foreground mt-1.5">Range: {min} – {max}{unit ? ` ${unit}` : ""}</p>
        )}
      </div>
    );
  }

  if (criterion.inputType === "range") {
    const toVal = rangeToValue ?? max;
    return (
      <div className="py-3 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className="text-xs text-muted-foreground mb-1">From</p>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={min}
                max={max}
                value={value}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) onChange(Math.min(toVal, Math.max(min, v)));
                }}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-base font-semibold text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid={`range-from-criterion-${criterion.id}`}
              />
              {unit && <span className="text-xs text-muted-foreground shrink-0">{unit}</span>}
            </div>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">To</p>
            <div className="flex items-center gap-1.5">
              <input
                type="number"
                min={min}
                max={max}
                value={toVal}
                onChange={e => {
                  const v = parseFloat(e.target.value);
                  if (!isNaN(v)) onRangeToChange?.(Math.max(value, Math.min(max, v)));
                }}
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-base font-semibold text-center focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid={`range-to-criterion-${criterion.id}`}
              />
              {unit && <span className="text-xs text-muted-foreground shrink-0">{unit}</span>}
            </div>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Allowed range: {min} – {max}{unit ? ` ${unit}` : ""}</p>
      </div>
    );
  }

  return (
    <div className="py-3 space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{min === 0 ? "Not relevant" : `${min}`}</span>
        <span className="font-bold text-primary text-2xl">
          {normaliseCriterion(value, min, max)}<span className="text-sm text-muted-foreground font-normal">/100</span>
        </span>
        <span className="text-muted-foreground">{max === 10 ? "Critical" : `${max}`}</span>
      </div>
      <Slider
        min={min}
        max={max}
        step={1}
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        className="w-full"
        data-testid={`slider-criterion-${criterion.id}`}
      />
    </div>
  );
}

function ACTPanel({ criterion }: { criterion: RadarCriterion }) {
  const [open, setOpen] = useState(false);
  const tabs = [
    { key: "awareness",  label: "Awareness",  text: criterion.actAwarenessText  },
    { key: "confidence", label: "Confidence", text: criterion.actConfidenceText },
    { key: "trust",      label: "Trust",      text: criterion.actTrustText      },
  ].filter(t => t.text);

  if (!tabs.length) return null;
  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        ACT Context
      </button>
      {open && (
        <Tabs defaultValue={tabs[0].key} className="mt-2">
          <TabsList className="h-7">
            {tabs.map(t => (
              <TabsTrigger key={t.key} value={t.key} className="text-xs px-3 h-6">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(t => (
            <TabsContent key={t.key} value={t.key} className="mt-2">
              <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-md px-3 py-2">
                {t.text}
              </p>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

function PSAPanel({ criterion }: { criterion: RadarCriterion }) {
  const [open, setOpen] = useState(false);
  const tabs = [
    { key: "problem",     label: "Problem",     text: criterion.psaProblemText     },
    { key: "solution",    label: "Solution",    text: criterion.psaSolutionText    },
    { key: "application", label: "Application", text: criterion.psaApplicationText },
  ].filter(t => t.text);

  if (!tabs.length) return null;
  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        PSA Context
      </button>
      {open && (
        <Tabs defaultValue={tabs[0].key} className="mt-2">
          <TabsList className="h-7">
            {tabs.map(t => (
              <TabsTrigger key={t.key} value={t.key} className="text-xs px-3 h-6">
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map(t => (
            <TabsContent key={t.key} value={t.key} className="mt-2">
              <p className="text-xs text-muted-foreground leading-relaxed bg-muted/40 rounded-md px-3 py-2">
                {t.text}
              </p>
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
}

export default function RadarEvaluatePage() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();

  // Re-open flow: if ?evaluation=:id is present, load an existing evaluation
  const existingEvalId = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("evaluation");
  }, [search]);

  const { data: radarData, isLoading: radarLoading } = useQuery<RadarDetail>({
    queryKey: [`/api/public/radars/${slug}`],
    enabled: !!slug,
  });

  // Fetch existing evaluation when re-opening
  const { data: existingEvalData } = useQuery<{ evaluation: RadarEvaluation; responses: import("@shared/schema").RadarResponse[]; criteria: RadarCriterion[] }>({
    queryKey: [`/api/public/evaluations/${existingEvalId}`],
    enabled: !!existingEvalId,
  });

  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState<Record<string, number>>({});
  const [weights, setWeights] = useState<Record<string, number>>({});
  const [multiSelections, setMultiSelections] = useState<Record<string, boolean[]>>({});
  const [rangeToValues, setRangeToValues] = useState<Record<string, number>>({});
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [precursorSelections, setPrecursorSelections] = useState<Record<string, string[]>>({});
  const [precursorScreenDone, setPrecursorScreenDone] = useState(false);
  const [skippedCriteria, setSkippedCriteria] = useState<Set<string>>(new Set());
  const [aboutOpen, setAboutOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<CriterionImage | null>(null);

  useEffect(() => { setAboutOpen(false); }, [currentStep]);

  // Pre-populate state from an existing evaluation (re-open flow)
  useEffect(() => {
    if (!existingEvalData || evaluationId) return;
    const { evaluation, responses: existingResponses } = existingEvalData;
    setEvaluationId(evaluation.id);
    // Restore access token from localStorage if stored
    const storedToken = localStorage.getItem(`radar-eval-token-${evaluation.id}`);
    if (storedToken) setAccessToken(storedToken);
    // Pre-populate response values, weights, and skipped set
    const newResponses: Record<string, number> = {};
    const newWeights: Record<string, number> = {};
    const newSkipped = new Set<string>();
    existingResponses.forEach(r => {
      newResponses[r.criterionId] = r.value;
      newWeights[r.criterionId] = r.userWeight ?? 1;
      if (r.skipped) newSkipped.add(r.criterionId);
    });
    setResponses(prev => ({ ...prev, ...newResponses }));
    setWeights(prev => ({ ...prev, ...newWeights }));
    setSkippedCriteria(newSkipped);
  }, [existingEvalData]);

  const createEvaluationMutation = useMutation({
    mutationFn: () => apiRequest("POST", `/api/public/radars/${slug}/evaluations`),
    onSuccess: async (res) => {
      const data: RadarEvaluation & { accessToken?: string } = await res.json();
      setEvaluationId(data.id);
      if (data.accessToken) {
        setAccessToken(data.accessToken);
        localStorage.setItem(`radar-eval-token-${data.id}`, data.accessToken);
      }
    },
    onError: () => toast({ title: "Failed to start evaluation", variant: "destructive" }),
  });

  const saveResponsesMutation = useMutation({
    mutationFn: (payload: { criterionId: string; value: number; userWeight: number; skipped?: boolean; skipNote?: string }[]) =>
      apiRequest(
        "PATCH",
        `/api/public/evaluations/${evaluationId}/responses`,
        { responses: payload },
        accessToken ? { "x-eval-token": accessToken } : undefined,
      ),
  });

  const completeMutation = useMutation({
    mutationFn: () =>
      apiRequest(
        "POST",
        `/api/public/evaluations/${evaluationId}/complete`,
        {},
        accessToken ? { "x-eval-token": accessToken } : undefined,
      ),
    onSuccess: async (res) => {
      // POST complete now returns { evaluation, responses, criteria }
      const data: { evaluation: RadarEvaluation } = await res.json();
      navigate(`/radar/${slug}/results/${data.evaluation.id}`);
    },
    onError: () => toast({ title: "Failed to complete evaluation", variant: "destructive" }),
  });

  const criteria = radarData?.criteria ?? [];
  const criteriaSets = radarData?.criteriaSets ?? [];
  const precursorDefinitions = radarData?.precursorDefinitions ?? [];
  const hasNonDefaultSets = criteriaSets.some(s => !s.isDefault);

  const filteredCriteria = useMemo(() => {
    if (!hasNonDefaultSets || !precursorScreenDone) return criteria;
    const matchingSetIds = new Set<string>(
      criteriaSets
        .filter(s => s.isDefault || (s.preconditions ?? []).every(p =>
          (precursorSelections[p.key] ?? []).includes(p.value)
        ))
        .map(s => s.id)
    );
    return criteria.filter(c => !c.criteriaSetId || matchingSetIds.has(c.criteriaSetId));
  }, [criteria, criteriaSets, precursorSelections, precursorScreenDone, hasNonDefaultSets]);

  // Initialise default response values when criteria load
  useEffect(() => {
    if (!criteria.length) return;
    setResponses(prev => {
      const merged: Record<string, number> = {};
      criteria.forEach(c => {
        merged[c.id] = prev[c.id] ?? Math.round(((c.minValue ?? 0) + (c.maxValue ?? 10)) / 2);
      });
      return merged;
    });
    setWeights(prev => {
      const merged: Record<string, number> = {};
      criteria.forEach(c => { merged[c.id] = prev[c.id] ?? 1; });
      return merged;
    });
    setMultiSelections(prev => {
      const merged: Record<string, boolean[]> = {};
      criteria.forEach(c => {
        if (c.inputType === "multi_select") {
          merged[c.id] = prev[c.id] ?? (c.criterionOptions ?? []).map(() => false);
        }
      });
      return { ...prev, ...merged };
    });
    setRangeToValues(prev => {
      const merged: Record<string, number> = {};
      criteria.forEach(c => {
        if (c.inputType === "range") {
          merged[c.id] = prev[c.id] ?? (c.maxValue ?? 10);
        }
      });
      return { ...prev, ...merged };
    });
  }, [criteria.length]);

  // Auto-create evaluation when radar loads (skip if re-opening an existing one)
  useEffect(() => {
    if (!radarLoading && criteria.length && !evaluationId && !createEvaluationMutation.isPending && !existingEvalId) {
      createEvaluationMutation.mutate();
    }
  }, [radarLoading, criteria.length, evaluationId, existingEvalId]);

  const debouncedSave = useCallback((criterionId: string, value: number, weight: number) => {
    if (!evaluationId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveResponsesMutation.mutate([{ criterionId, value, userWeight: weight }]);
    }, 500);
  }, [evaluationId, accessToken]);

  const handleValueChange = useCallback((criterionId: string, value: number) => {
    setResponses(prev => {
      debouncedSave(criterionId, value, weights[criterionId] ?? 1);
      return { ...prev, [criterionId]: value };
    });
  }, [weights, debouncedSave]);

  const handleWeightChange = useCallback((criterionId: string, weight: number) => {
    setWeights(prev => {
      debouncedSave(criterionId, responses[criterionId] ?? 0, weight);
      return { ...prev, [criterionId]: weight };
    });
  }, [responses, debouncedSave]);

  const handleNext = () => {
    const criterion = filteredCriteria[currentStep];
    if (criterion && evaluationId && !skippedCriteria.has(criterion.id)) {
      saveResponsesMutation.mutate([{
        criterionId: criterion.id,
        value: responses[criterion.id] ?? Math.round(((criterion.minValue ?? 0) + (criterion.maxValue ?? 10)) / 2),
        userWeight: weights[criterion.id] ?? 1,
      }]);
    }
    setCurrentStep(s => s + 1);
  };

  const handleBack = () => setCurrentStep(s => Math.max(0, s - 1));

  const handleSkip = (criterionId: string) => {
    const newSkipped = new Set(skippedCriteria);
    newSkipped.add(criterionId);
    setSkippedCriteria(newSkipped);
    const crit = filteredCriteria.find(c => c.id === criterionId);
    const midpoint = crit ? Math.round(((crit.minValue ?? 0) + (crit.maxValue ?? 10)) / 2) : 0;
    if (evaluationId) {
      saveResponsesMutation.mutate([{ criterionId, value: midpoint, userWeight: 1, skipped: true }]);
    }
    setCurrentStep(s => Math.min(s + 1, filteredCriteria.length - 1));
  };

  const handleComplete = () => {
    if (!evaluationId) return;
    const payload = filteredCriteria.map(c => ({
      criterionId: c.id,
      value: responses[c.id] ?? Math.round(((c.minValue ?? 0) + (c.maxValue ?? 10)) / 2),
      userWeight: weights[c.id] ?? 1,
      skipped: skippedCriteria.has(c.id),
    }));
    saveResponsesMutation.mutate(payload, {
      onSuccess: () => completeMutation.mutate(),
    });
  };

  // Chart includes responses up to AND including the current step (excludes skipped criteria)
  const chartData = useMemo(
    () => buildChartData(filteredCriteria.filter(c => !skippedCriteria.has(c.id)), responses, currentStep),
    [filteredCriteria, skippedCriteria, responses, currentStep],
  );

  if (radarLoading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 max-w-5xl mx-auto px-4 py-12 w-full space-y-6">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-2 w-full rounded" />
          <div className="grid lg:grid-cols-[1fr_300px] gap-8">
            <Skeleton className="h-80 rounded-xl" />
            <Skeleton className="h-72 rounded-xl" />
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (!radarData) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex flex-col items-center justify-center p-6">
          <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
          <h1 className="text-xl font-bold mb-2">Radar Not Found</h1>
          <Button asChild variant="outline">
            <Link href="/radar"><ArrowLeft className="mr-2 h-4 w-4" />Back to Radar</Link>
          </Button>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const { config } = radarData;

  // Precursor screen: shown before the evaluation starts if the radar has non-default criteria sets
  if (hasNonDefaultSets && !precursorScreenDone) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <main className="flex-1 max-w-2xl mx-auto px-4 py-12 w-full">
          <Link href={`/radar/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-8">
            <ArrowLeft className="h-4 w-4" />
            {config.title}
          </Link>
          <div className="mb-2 flex items-center gap-2">
            <Target className="h-5 w-5 text-primary" />
            <span className="text-xs font-semibold text-primary uppercase tracking-wider">Describe your application</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Tell us about your use case</h1>
          <p className="text-muted-foreground mb-8">
            We'll show you only the criteria that are relevant to your specific application.
          </p>
          <div className="space-y-8">
            {precursorDefinitions.map(def => (
              <div key={def.id}>
                <p className="font-medium mb-3">{def.label}</p>
                <div className="flex flex-wrap gap-2">
                  {(def.options ?? []).map(opt => {
                    const selected = (precursorSelections[def.key] ?? []).includes(opt);
                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          setPrecursorSelections(prev => {
                            const current = prev[def.key] ?? [];
                            return {
                              ...prev,
                              [def.key]: selected
                                ? current.filter(v => v !== opt)
                                : [...current, opt],
                            };
                          });
                        }}
                        className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                          selected
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background border-border hover:border-primary/50 hover:bg-muted"
                        }`}
                        data-testid={`precursor-option-${def.key}-${opt}`}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 flex items-center gap-4">
            <Button
              onClick={() => setPrecursorScreenDone(true)}
              data-testid="button-begin-evaluation"
            >
              Begin Evaluation
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
            <button
              onClick={() => { setPrecursorSelections({}); setPrecursorScreenDone(true); }}
              className="text-sm text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
              data-testid="button-skip-precursor"
            >
              Skip — show all criteria
            </button>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const criterion = filteredCriteria[currentStep];
  const isLastStep = currentStep === filteredCriteria.length - 1;
  const progressPct = filteredCriteria.length > 0 ? Math.round((currentStep / filteredCriteria.length) * 100) : 0;
  const currentWeight = weights[criterion?.id ?? ""] ?? 1;

  if (!criterion) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <SiteHeader />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">

        {/* Progress header */}
        <div className="mb-6">
          <Link href={`/radar/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-3">
            <ArrowLeft className="h-4 w-4" />
            {config.title}
          </Link>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-muted-foreground">
                Step {currentStep + 1} of {filteredCriteria.length}
                {skippedCriteria.size > 0 && (
                  <span className="ml-1 text-muted-foreground/60">({skippedCriteria.size} skipped)</span>
                )}
              </span>
            </div>
            <span className="text-sm text-muted-foreground">{progressPct}% complete</span>
          </div>
          <Progress value={progressPct} className="h-2" data-testid="progress-evaluation" />
        </div>

        <div className="lg:grid lg:grid-cols-[1fr_300px] lg:gap-8 lg:items-start">
          {/* Criterion card */}
          <div>
            <Card className="mb-4" data-testid={`criterion-card-${criterion.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge variant="outline" className="text-xs font-mono">
                    {currentStep + 1}/{filteredCriteria.length}
                  </Badge>
                  {skippedCriteria.has(criterion.id) && (
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0 text-muted-foreground">Skipped</Badge>
                  )}
                  {criterion.isCritical && (
                    <Badge variant="destructive" className="text-[10px] px-1.5 py-0">Critical</Badge>
                  )}
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0 capitalize">
                    {criterion.inputType}
                  </Badge>
                </div>
                <CardTitle className="text-xl">{criterion.name}</CardTitle>
                {criterion.description && (
                  <p className="text-sm text-muted-foreground mt-1.5">{criterion.description}</p>
                )}
                <ACTPanel criterion={criterion} />
                <PSAPanel criterion={criterion} />

                {/* About this criterion — educational collapsible */}
                {(criterion.educationalContent || (criterion.images?.length ?? 0) > 0 || (criterion.resources?.length ?? 0) > 0) && (
                  <div className="mt-3 border rounded-lg overflow-hidden">
                    <button
                      className="w-full flex items-center gap-2 px-3 py-2.5 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
                      onClick={() => setAboutOpen(v => !v)}
                      data-testid={`button-about-criterion-${criterion.id}`}
                    >
                      <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span className="text-xs font-medium flex-1">About this criterion</span>
                      {aboutOpen ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />}
                    </button>
                    {aboutOpen && (
                      <div className="px-3 py-3 space-y-4 bg-background">
                        {criterion.educationalContent && (
                          <div className="prose prose-sm dark:prose-invert max-w-none text-sm leading-relaxed">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                              {criterion.educationalContent}
                            </ReactMarkdown>
                          </div>
                        )}
                        {(criterion.images?.length ?? 0) > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Reference Images</p>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {criterion.images!.map(img => (
                                <button
                                  key={img.id}
                                  onClick={() => setLightboxImage(img)}
                                  className="shrink-0 focus:outline-none"
                                  data-testid={`button-criterion-image-${img.id}`}
                                >
                                  <img
                                    src={img.storageUrl}
                                    alt={img.altText ?? img.caption ?? "Reference image"}
                                    className="h-24 w-auto max-w-[180px] object-cover rounded-md border hover:opacity-90 transition-opacity"
                                  />
                                  {img.caption && (
                                    <p className="text-[10px] text-muted-foreground mt-0.5 text-left truncate max-w-[180px]">{img.caption}</p>
                                  )}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        {(criterion.resources?.length ?? 0) > 0 && (
                          <div>
                            <p className="text-xs font-medium text-muted-foreground mb-2">Further Reading</p>
                            <div className="space-y-1.5">
                              {criterion.resources!.map(r => (
                                <a
                                  key={r.id}
                                  href={r.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-start gap-2 rounded-md border px-3 py-2 text-sm hover:bg-muted/50 transition-colors group"
                                  data-testid={`link-resource-${r.id}`}
                                >
                                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0 group-hover:text-primary transition-colors" />
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium leading-tight">{r.label}</p>
                                    {r.description && <p className="text-xs text-muted-foreground mt-0.5">{r.description}</p>}
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </CardHeader>

              <CardContent>
                {/* Rating input */}
                <div className="mb-4">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Your rating</p>
                  <CriterionInput
                    criterion={criterion}
                    value={responses[criterion.id] ?? Math.round(((criterion.minValue ?? 0) + (criterion.maxValue ?? 10)) / 2)}
                    onChange={v => handleValueChange(criterion.id, v)}
                    multiSelections={multiSelections[criterion.id]}
                    onMultiSelectChange={selections => {
                      setMultiSelections(prev => ({ ...prev, [criterion.id]: selections }));
                      handleValueChange(criterion.id, selections.filter(Boolean).length);
                    }}
                    rangeToValue={rangeToValues[criterion.id]}
                    onRangeToChange={v => setRangeToValues(prev => ({ ...prev, [criterion.id]: v }))}
                  />
                </div>

                {/* Importance weight — critical toggle (only when admin hasn't pre-flagged it) */}
                {!criterion.isCritical && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Critical for my application</p>
                        <p className="text-xs text-muted-foreground/70 mt-0.5">
                          Double-weights this criterion in your score
                        </p>
                      </div>
                      <Switch
                        checked={currentWeight >= 2}
                        onCheckedChange={checked => handleWeightChange(criterion.id, checked ? 2 : 1)}
                        data-testid={`weight-toggle-${criterion.id}`}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Navigation */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Button
                  variant="outline"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                  data-testid="button-prev-step"
                >
                  <ArrowLeft className="mr-1.5 h-4 w-4" />
                  Previous
                </Button>

                {isLastStep ? (
                  <Button
                    onClick={handleComplete}
                    disabled={completeMutation.isPending || saveResponsesMutation.isPending || !evaluationId}
                    data-testid="button-complete-evaluation"
                  >
                    {(completeMutation.isPending || saveResponsesMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {completeMutation.isPending ? "Calculating…" : "Submit & View Results"}
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    disabled={!evaluationId}
                    data-testid="button-next-step"
                  >
                    Next
                    <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Button>
                )}
              </div>

              {!skippedCriteria.has(criterion.id) && (
                <div className="text-center">
                  <button
                    onClick={() => handleSkip(criterion.id)}
                    className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                    data-testid={`button-skip-criterion-${criterion.id}`}
                  >
                    This criterion doesn't apply to my use case — skip it
                  </button>
                  {criterion.skipGuidance && (
                    <p className="text-[11px] text-muted-foreground/70 mt-1 max-w-sm mx-auto">
                      {criterion.skipGuidance}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Live radar chart — sticky sidebar */}
          <div className="lg:sticky lg:top-6 mt-6 lg:mt-0">
            <Card>
              <CardHeader className="pb-0">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  Live Radar
                </CardTitle>
              </CardHeader>
              <CardContent>
                {chartData.length >= 3 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 16 }}>
                      <PolarGrid stroke="hsl(var(--border))" />
                      <PolarAngleAxis
                        dataKey="subject"
                        tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }}
                      />
                      <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                      <RechartsRadar
                        name="Score"
                        dataKey="value"
                        stroke="hsl(var(--primary))"
                        fill="hsl(var(--primary))"
                        fillOpacity={0.25}
                        strokeWidth={2}
                        dot={{ r: 3, fill: "hsl(var(--primary))" }}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[260px] flex items-center justify-center text-xs text-muted-foreground text-center px-4">
                    Chart builds as you rate criteria
                  </div>
                )}
                <p className="text-xs text-center text-muted-foreground mt-1">
                  {currentStep + 1 - skippedCriteria.size} of {filteredCriteria.length - skippedCriteria.size} rated
                  {skippedCriteria.size > 0 && `, ${skippedCriteria.size} skipped`}
                </p>
              </CardContent>
            </Card>

            {config.matchThreshold && (
              <Card className="mt-3 bg-muted/30">
                <CardContent className="pt-3 pb-3">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">Match threshold</span>
                    <Badge variant="outline" className="text-xs">{config.matchThreshold}%</Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Scores above this level indicate a strong product match.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />

      {/* Image lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="lightbox-overlay"
        >
          <button
            className="absolute top-4 right-4 h-9 w-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white"
            onClick={() => setLightboxImage(null)}
            data-testid="button-close-lightbox"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <div onClick={e => e.stopPropagation()} className="max-w-3xl max-h-[80vh] flex flex-col items-center gap-3">
            <img
              src={lightboxImage.storageUrl}
              alt={lightboxImage.altText ?? lightboxImage.caption ?? "Reference image"}
              className="max-h-[70vh] max-w-full object-contain rounded-lg"
            />
            {lightboxImage.caption && (
              <p className="text-sm text-white/80 text-center">{lightboxImage.caption}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
